import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { NotificationRow } from "../components/NotificationRow";
import { docUrl, formatBytes } from "../lib/docUrl";
import {
  NOTIFICATION_CATEGORIES,
  academicYearOf,
  FILTERABLE_ACADEMIC_YEARS,
  attachmentAriaLabel,
  dateParts,
  getNotifications,
  isExternalHref,
  registerPage,
  type Notification,
} from "../lib/notifications";

const PAGE_SIZE = 20;

/**
 * The leading pinned notice, rendered into the hero's second column in place
 * of the banner image. Same shape as the home lead panel, but on a lighter
 * navy with a gold edge so it reads as a card against the navy hero rather
 * than disappearing into it.
 */
function HeroPinned({ item }: { item: Notification }) {
  const { day, month, year } = dateParts(item.date);
  return (
    <article className="notif-hero-pinned">
      <div className="notif-hero-pinned-meta">
        <span className="notif-pin">Pinned</span>
        <span className="notif-hero-pinned-date">
          {item.category} · {day} {month} {year}
        </span>
      </div>
      <h2 className="notif-hero-pinned-title">{item.title}</h2>
      {item.summary && <p className="notif-hero-pinned-summary">{item.summary}</p>}
      {item.attachment && (
        <a
          className="notif-hero-pinned-doc"
          href={docUrl(item.attachment.key)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={attachmentAriaLabel(item)}
        >
          <span aria-hidden="true">↓</span> Circular ·{" "}
          {item.attachment.kind.toUpperCase()} {formatBytes(item.attachment.bytes)}
        </a>
      )}
      {!item.attachment && item.href && isExternalHref(item.href) && (
        <a
          className="notif-hero-pinned-doc"
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open: ${item.title} (external site)`}
        >
          Open <span aria-hidden="true">↗</span>
        </a>
      )}
      {!item.attachment && item.href && !isExternalHref(item.href) && (
        <Link className="notif-hero-pinned-doc" to={item.href} aria-label={`Read: ${item.title}`}>
          Read <span aria-hidden="true">→</span>
        </Link>
      )}
    </article>
  );
}

// Safari throttles history.replaceState to roughly 100 calls per 30s and
// throws SecurityError past it. A write on every keystroke blows through
// that in one fast sentence, so the URL write is debounced by this much
// while the input itself stays immediate (see `q` / `onSearch` below).
const SEARCH_DEBOUNCE_MS = 300;

export default function Notifications() {
  const [params, setParams] = useSearchParams();
  const all = useMemo(() => getNotifications(), []);

  const category = (() => {
    const p = params.get("category");
    if (!p) return "ALL";
    // Case-insensitive so the documented ?category=fees URL actually works;
    // resolves to the canonical capitalised form so every downstream
    // comparison (filtering, the active facet, setFilter) can keep treating
    // `category` as one of NOTIFICATION_CATEGORIES verbatim.
    const match = NOTIFICATION_CATEGORIES.find((c) => c.toLowerCase() === p.toLowerCase());
    return match ?? "ALL";
  })();

  const years = FILTERABLE_ACADEMIC_YEARS;
  const year = (() => {
    const p = params.get("year");
    return p && years.includes(p) ? p : "ALL";
  })();

  // The search box mirrors the URL's `q`, not just its own initial value:
  // `category` and `year` above are derived fresh every render, but a plain
  // `useState(params.get("q") ?? "")` only seeds once. Navigating from
  // /notifications?q=fee to /notifications on the same route (e.g. the
  // footer link) reuses this element without remounting it, so a
  // seed-once state would keep showing "fee" and keep filtering by it while
  // the URL claims no filter. `q` is local state so typing is immediate;
  // the effect below resyncs it whenever the URL's `q` changes from
  // somewhere other than our own debounced write (see onSearch).
  const paramQ = params.get("q") ?? "";
  const [q, setQ] = useState(paramQ);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Holds the params string this component itself last wrote (via setFilter
  // or the debounced search commit below). The resync effect compares the
  // current params against this ref, rather than consuming a one-shot flag,
  // to tell "the URL is what we last wrote ourselves" — where a
  // still-scheduled timer must be left alone — apart from "the URL changed
  // for some other reason" (a navigation from elsewhere, e.g. a nav link or
  // another component's `navigate()` call), where a pending timer is for an
  // abandoned query and must be cancelled. A boolean latch consumed by the
  // effect doesn't work here: a content no-op write (e.g. clicking an
  // already-active facet) still schedules the effect's deps to be
  // unchanged, so the effect never runs to reset the flag, stranding it
  // `true` and misclassifying the next external navigation as our own. A
  // ref holding the last-written string needs no reset step: an unchanged
  // write leaves the ref already equal to the current params, and it will
  // legitimately compare unequal the next time an external navigation lands.
  const lastOwnWrite = useRef(params.toString());
  const paramsKey = params.toString();
  useEffect(() => {
    setQ(paramQ);
    if (paramsKey === lastOwnWrite.current) {
      return;
    }
    // A URL change we didn't make ourselves (e.g. navigating away mid-type)
    // means any still-pending debounced write is for an abandoned query;
    // letting it fire now would silently resurrect that query into whatever
    // URL exists by then.
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
      searchTimer.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, paramQ]);

  const [shown, setShown] = useState(PAGE_SIZE);

  /** Write one filter into the URL, dropping it when set back to ALL. */
  const setFilter = (key: "category" | "year", value: string) => {
    const next = new URLSearchParams(params);
    if (value === "ALL") next.delete(key);
    else next.set(key, value);
    lastOwnWrite.current = next.toString();
    setParams(next, { replace: true });
    setShown(PAGE_SIZE);
  };

  // `setParams` is itself a `useCallback` closed over the `searchParams`
  // snapshot from the render that created it (deps: [navigate, searchParams]).
  // A pending `setTimeout` captured that closure at schedule time, so neither
  // closing over `params` directly nor calling `setParams`'s own functional
  // updater sees a category/year click that lands during the debounce
  // window — both resolve `prev` against the same stale snapshot. Reading
  // through a ref that is refreshed every render is the only way the timeout
  // callback sees live params/setParams.
  const latest = useRef({ params, setParams });
  latest.current = { params, setParams };

  useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );

  const onSearch = (value: string) => {
    setQ(value);
    setShown(PAGE_SIZE);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const { params: live, setParams: set } = latest.current;
      const next = new URLSearchParams(live);
      if (value.trim() === "") next.delete("q");
      else next.set("q", value);
      lastOwnWrite.current = next.toString();
      set(next, { replace: true });
      searchTimer.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((n) => {
      if (category !== "ALL" && n.category !== category) return false;
      if (year !== "ALL" && academicYearOf(n.date) !== year) return false;
      if (needle && !n.title.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [all, category, year, q]);

  // The leading pinned notice is promoted into the hero, in place of the
  // banner image, so the most important circular is the first thing on the
  // page rather than a row in the register. Chosen from the unfiltered
  // catalogue so it stays put as the reader filters — it is page furniture,
  // not a result.
  const heroPinned = all.find((n) => n.pinned) ?? null;
  const isFiltered = category !== "ALL" || year !== "ALL" || q.trim() !== "";

  // Unfiltered, the promoted notice is removed from the register so it is not
  // shown twice. Once a filter is active the register becomes the
  // authoritative result set and includes it again — otherwise filtering to
  // its category would show an empty list while the notice sits in the hero.
  const register =
    heroPinned && !isFiltered ? filtered.filter((n) => n.id !== heroPinned.id) : filtered;

  // Pinned notices are exempt from pagination: a page of pinned circulars
  // would otherwise push every recent notice behind a click. Extracted as a
  // pure helper (lib/notifications.ts) so this boundary can be unit-tested
  // against a synthetic dataset large enough to cross PAGE_SIZE.
  const { pinned, visible, hasMore } = registerPage(register, shown);

  // Each facet count reflects every OTHER active filter (year, search) but
  // never its own category, so a year- or search-narrowed view still adds up
  // to what's actually on screen instead of the whole catalogue. "All
  // notifications" is the same computation with no category applied at all.
  const counts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const withoutCategory = all.filter((n) => {
      if (year !== "ALL" && academicYearOf(n.date) !== year) return false;
      if (needle && !n.title.toLowerCase().includes(needle)) return false;
      return true;
    });
    const m: Record<string, number> = { ALL: withoutCategory.length };
    NOTIFICATION_CATEGORIES.forEach((c) => {
      m[c] = withoutCategory.filter((n) => n.category === c).length;
    });
    return m;
  }, [all, year, q]);

  return (
    <>
      <PageHero
        variant="hero-editorial"
        eyebrow="Notice board"
        title="Circulars, notices and official announcements."
        lead="Every notification issued by the institute, newest first. Filter by category or academic year, or search by title."
        crumbs={[{ label: "Notifications" }]}
        image={null}
        aside={heroPinned ? <HeroPinned item={heroPinned} /> : undefined}
      />

      <section className="section">
        <div className="container">
          <div className="notif-shell">
            <aside className="notif-rail" aria-label="Filter notifications">
              <div className="notif-rail-section">
                <label className="notif-rail-label" htmlFor="notif-q">
                  Search
                </label>
                <input
                  id="notif-q"
                  className="notif-search"
                  type="search"
                  value={q}
                  placeholder="Title or keyword…"
                  onChange={(e) => onSearch(e.target.value)}
                />
              </div>

              <div className="notif-rail-section">
                <div className="notif-rail-label">Category</div>
                <ul className="notif-rail-list">
                  <li>
                    <button
                      type="button"
                      className={category === "ALL" ? "active" : ""}
                      aria-pressed={category === "ALL"}
                      onClick={() => setFilter("category", "ALL")}
                    >
                      <span>All notifications</span>
                      <span className="notif-rail-count">{counts.ALL}</span>
                    </button>
                  </li>
                  {NOTIFICATION_CATEGORIES.map((c) => (
                    <li key={c}>
                      <button
                        type="button"
                        className={category === c ? "active" : ""}
                        aria-pressed={category === c}
                        onClick={() => setFilter("category", c)}
                      >
                        <span>{c}</span>
                        <span className="notif-rail-count">{counts[c]}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="notif-rail-section">
                <div className="notif-rail-label">Academic year</div>
                <div className="notif-chips">
                  <button
                    type="button"
                    className={"notif-chip" + (year === "ALL" ? " active" : "")}
                    aria-pressed={year === "ALL"}
                    onClick={() => setFilter("year", "ALL")}
                  >
                    All years
                  </button>
                  {years.map((y) => (
                    <button
                      key={y}
                      type="button"
                      className={"notif-chip" + (year === y ? " active" : "")}
                      aria-pressed={year === y}
                      onClick={() => setFilter("year", y)}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className="notif-register">
              {/* No "Pinned" group heading any more: the leading pinned notice
                  is promoted into the hero. Any remaining pinned notices still
                  sort to the top and carry their own Pinned marker on the row,
                  which is enough to explain the ordering. */}
              {pinned.map((item) => (
                <NotificationRow key={item.id} item={item} />
              ))}
              {visible.map((item) => (
                <NotificationRow key={item.id} item={item} />
              ))}

              {filtered.length === 0 && (
                <p className="notif-empty">
                  No notifications match these filters.
                </p>
              )}

              {filtered.length > 0 && (
                <div className="notif-pager">
                  <span className="notif-tally">
                    Showing {pinned.length + visible.length} of {filtered.length}
                  </span>
                  {hasMore && (
                    <button
                      type="button"
                      className="notif-more"
                      onClick={() => setShown((s) => s + PAGE_SIZE)}
                    >
                      Load more <span aria-hidden="true">↓</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
